/**
 * Shared comparison chart rendering and Smart / Classic icon switching.
 */
(function () {
  const TICK =
    '<svg class="cmp-check" viewBox="0 0 32 32" width="32" height="32" aria-hidden="true"><circle cx="16" cy="16" r="16" fill="#1e3a5f"/><path d="M10 16.5l4 4 8-8" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>';

  const ICON_SMART =
    '<svg viewBox="0 0 30 30" width="30" height="30" aria-hidden="true"><circle cx="15" cy="15" r="12" fill="#1e3a5f"/><rect x="10.5" y="7" width="9" height="16" rx="1.5" fill="none" stroke="#fff" stroke-width="1.3"/><circle cx="15" cy="19.5" r="1" fill="#fff"/><line x1="13" y1="8.5" x2="17" y2="8.5" stroke="#fff" stroke-width="0.8" stroke-linecap="round"/></svg>';

  const ICON_CLASSIC =
    '<svg viewBox="0 0 30 30" width="30" height="30" aria-hidden="true"><circle cx="15" cy="15" r="12" fill="#f5f5f5"/><rect x="10.5" y="7" width="9" height="16" rx="1.5" fill="none" stroke="#888" stroke-width="1.3"/><circle cx="15" cy="19.5" r="1" fill="#888"/><line x1="13" y1="8.5" x2="17" y2="8.5" stroke="#888" stroke-width="0.8" stroke-linecap="round"/><line x1="7" y1="23" x2="23" y2="7" stroke="#888" stroke-width="1.3" stroke-linecap="round"/></svg>';

  const DASH =
    '<span class="cmp-dash"><span class="visually-hidden">Not available</span><span aria-hidden="true">\u2014</span></span>';

  /**
   * Legacy-only: maps old free-text keys to canonical labels and curates which
   * rows show. New array-format specs carry their own label/type/value/subtext
   * and are not filtered by this list.
   */
  const SPEC_FIELDS = [
    { label: 'Room Size', match: 'room size' },
    { label: 'CADR', match: 'cadr' },
    { label: 'Wi-Fi / App', match: 'wifi' },
    { label: 'Fan Speeds', match: 'fan speed' },
    { label: '3-Stage Filtration with UltraHEPA\u00AE', match: 'filter' },
    { label: 'Air Quality Indicator', match: 'air quality indicator' },
    { label: 'PM2.5 Sensor', match: 'particle sensor' },
    { label: 'Auto-Mode', match: 'auto mode' },
  ];

  function stripHtml(str) {
    const tmp = document.createElement('span');
    tmp.innerHTML = str;
    return tmp.textContent || tmp.innerText || str;
  }

  function rowKey(label) {
    return stripHtml(String(label == null ? '' : label))
      .trim()
      .toLowerCase();
  }

  function escapeAttr(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escapeHtml(str) {
    return escapeAttr(str);
  }

  function parseRaw(raw) {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        try {
          return JSON.parse(raw.replace(/=>/g, ':'));
        } catch {
          return null;
        }
      }
    }
    return null;
  }

  function makeRow(label, type, value, subtext) {
    return {
      label: String(label == null ? '' : label).trim(),
      type: type,
      value: value == null ? '' : String(value),
      subtext: subtext == null ? '' : String(subtext),
    };
  }

  function resolveType(type, value, subtext) {
    const t = String(type == null ? '' : type)
      .trim()
      .toLowerCase();
    if (t === 'checkmark' || t === 'check' || t === 'tick' || t === 'yes') return 'checkmark';
    if (t === 'text_subtext' || t === 'stacked' || t === 'subtext') return 'text_subtext';
    if (t === 'text') return 'text';
    const v = value == null ? '' : String(value).trim();
    if (/^(yes|true)$/i.test(v)) return 'checkmark';
    if (subtext != null && String(subtext).trim()) return 'text_subtext';
    return 'text';
  }

  /** Convert a legacy "key": "value" pair into a normalized row. */
  function legacyRow(key, val) {
    const str = val == null ? '' : String(val).trim();
    const field = SPEC_FIELDS.find((f) => rowKey(key).includes(f.match));
    const label = field ? field.label : stripHtml(key);
    if (/^yes$/i.test(str)) return makeRow(label, 'checkmark', '', '');
    const parts = str
      .split(/<br\s*\/?>|\n/gi)
      .map((part) => part.trim())
      .filter(Boolean);
    if (field && field.match === 'fan speed' && parts.length === 1 && /^\d+$/.test(parts[0])) {
      return makeRow(label, 'text_subtext', parts[0], 'Speed Options');
    }
    if (parts.length > 1) {
      return makeRow(label, 'text_subtext', parts[0], parts.slice(1).join('<br>'));
    }
    return makeRow(label, 'text', parts[0] || '', '');
  }

  /**
   * Normalize a specs field into an ordered array of rows:
   *   { label, type: 'checkmark'|'text'|'text_subtext', value, subtext }
   * - New format: an array of row objects (rendered as authored).
   * - Legacy format: an object of "label": "value" (curated to SPEC_FIELDS, canonical labels).
   */
  function normalizeSpecs(raw) {
    const parsed = parseRaw(raw);
    if (!parsed) return [];

    if (Array.isArray(parsed)) {
      return parsed
        .map((entry) => {
          if (!entry || typeof entry !== 'object') return null;
          const label = entry.label != null ? entry.label : entry.name;
          if (label == null || String(label).trim() === '') return null;
          return makeRow(label, resolveType(entry.type, entry.value, entry.subtext), entry.value, entry.subtext);
        })
        .filter(Boolean);
    }

    if (typeof parsed === 'object') {
      const rows = [];
      SPEC_FIELDS.forEach((field) => {
        const key = Object.keys(parsed).find((k) => rowKey(k).includes(field.match));
        if (key != null) rows.push(legacyRow(key, parsed[key]));
      });
      return rows;
    }

    return [];
  }

  function rowHasContent(row) {
    if (!row) return false;
    if (row.type === 'checkmark') return true;
    return Boolean((row.value && row.value.trim()) || (row.subtext && row.subtext.trim()));
  }

  function formatRow(row) {
    if (!row) return DASH;
    if (row.type === 'checkmark') {
      const checkSub = (row.subtext || '').toString().trim();
      const tickMarkup = `<span class="visually-hidden">Yes</span>${TICK}`;
      if (!checkSub) return tickMarkup;
      return (
        '<span class="cmp-val cmp-val--stacked cmp-val--check">' +
        tickMarkup +
        `<span class="cmp-val__secondary">${checkSub}</span>` +
        '</span>'
      );
    }
    const primary = (row.value || '').toString().trim();
    const secondary = (row.subtext || '').toString().trim();
    if (!primary && !secondary) return DASH;
    if (row.type === 'text_subtext' || secondary) {
      return (
        '<span class="cmp-val cmp-val--stacked">' +
        `<span class="cmp-val__primary">${primary}</span>` +
        (secondary ? `<span class="cmp-val__secondary">${secondary}</span>` : '') +
        '</span>'
      );
    }
    return `<span class="cmp-val"><span class="cmp-val__primary">${primary}</span></span>`;
  }

  function formatCell(row, productTitle) {
    const title = escapeHtml(stripHtml(productTitle || ''));
    return `<span class="visually-hidden">${title}: </span>${formatRow(row)}`;
  }

  function getVariantTypes(variants) {
    const hasSmart = variants.some((v) => /smart/i.test(v.title) || v.type === 'smart');
    const hasClassic = variants.some((v) => /classic/i.test(v.title) || v.type === 'classic');
    return { hasSmart, hasClassic };
  }

  function getActiveType(variant) {
    if (!variant) return 'classic';
    if (variant.type === 'smart' || variant.type === 'classic') return variant.type;
    if (/smart/i.test(variant.title)) return 'smart';
    if (/classic/i.test(variant.title)) return 'classic';
    return 'classic';
  }

  function productHasSmartToggle(product) {
    const variants = product.variants || [];
    const { hasSmart, hasClassic } = getVariantTypes(variants);
    return Boolean(product.has_smart_variant && hasSmart && hasClassic);
  }

  function getInitialType(product) {
    if (!productHasSmartToggle(product)) return 'classic';
    return 'classic';
  }

  function getMergedRows(product, type) {
    const chart = product.chart || {};
    const shared = normalizeSpecs(chart.shared);
    const variation = normalizeSpecs(chart[type]);
    const map = new Map();
    shared.forEach((row) => map.set(rowKey(row.label), row));
    variation.forEach((row) => map.set(rowKey(row.label), row));
    return [...map.values()];
  }

  function findRow(product, type, key) {
    return getMergedRows(product, type).find((row) => rowKey(row.label) === key) || null;
  }

  /**
   * Ordered, de-duplicated list of rows to render across all products.
   * Considers both Classic and Smart so variant-only rows (e.g. sensors) exist
   * on first render even though the chart starts on Classic.
   */
  function buildRowOrder(products) {
    const order = [];
    const seen = new Set();
    products.forEach((product) => {
      ['classic', 'smart'].forEach((type) => {
        getMergedRows(product, type).forEach((row) => {
          const key = rowKey(row.label);
          if (!key || seen.has(key)) return;
          seen.add(key);
          order.push({ key: key, label: row.label });
        });
      });
    });
    return order.filter((def) =>
      products.some((product) =>
        ['classic', 'smart'].some((type) => rowHasContent(findRow(product, type, def.key)))
      )
    );
  }

  function findVariantByType(variants, type) {
    return variants.find((v) => getActiveType(v) === type) || variants[0] || {};
  }

  function buildBadges(product, activeType) {
    const variants = product.variants || [];
    const showToggle = productHasSmartToggle(product);

    if (!showToggle) {
      return `<div class="cmp-badges cmp-badges--classic-only" data-active="classic" role="group" aria-label="Purifier type">
        <div class="cmp-badge cmp-badge--active cmp-badge--static" data-type="classic" aria-current="true">
          <div class="cmp-badge__icon cmp-badge__icon--active">${ICON_CLASSIC}</div>
          <span class="cmp-badge__label">Classic</span>
        </div>
      </div>`;
    }

    let html = `<div class="cmp-badges" data-active="${activeType}" role="group" aria-label="Purifier type">`;

    ['classic', 'smart'].forEach((type) => {
      const isActive = type === activeType;
      const icon = type === 'smart' ? ICON_SMART : ICON_CLASSIC;
      const label = type === 'smart' ? 'Smart' : 'Classic';
      html += `<button type="button" class="cmp-badge${isActive ? ' cmp-badge--active' : ''}" data-type="${type}" aria-pressed="${isActive}" aria-label="${label}">`;
      html += `<div class="cmp-badge__icon${isActive ? ' cmp-badge__icon--active' : ''}">${icon}</div>`;
      html += `<span class="cmp-badge__label">${isActive ? label : ''}</span>`;
      html += '</button>';
    });

    html += '</div>';
    return html;
  }

  function updateBadges(col, activeType, showToggle) {
    const badgesWrap = col.querySelector('.cmp-badges');
    if (!badgesWrap || badgesWrap.classList.contains('cmp-badges--classic-only')) return;

    badgesWrap.dataset.active = activeType;
    badgesWrap.querySelectorAll('.cmp-badge').forEach((badge) => {
      const type = badge.dataset.type;
      const isActive = type === activeType;
      badge.classList.toggle('cmp-badge--active', isActive);
      badge.setAttribute('aria-pressed', String(isActive));
      const icon = badge.querySelector('.cmp-badge__icon');
      if (icon) icon.classList.toggle('cmp-badge__icon--active', isActive);
      const label = badge.querySelector('.cmp-badge__label');
      if (label) label.textContent = isActive ? (type === 'smart' ? 'Smart' : 'Classic') : '';
    });
  }

  function updateColumn(product, col, type, columnIndex, chartRoot) {
    const variant = findVariantByType(product.variants || [], type);
    const img = col.querySelector('.cmp-chart__product-img');
    if (img && variant.image) img.src = variant.image;
    const priceEl = col.querySelector('.cmp-chart__price');
    if (priceEl) priceEl.textContent = variant.price || product.price;
    const wasEl = col.querySelector('.cmp-chart__price-was');
    if (wasEl) {
      if (variant.compare_at_price) {
        wasEl.textContent = variant.compare_at_price;
        wasEl.hidden = false;
      } else {
        wasEl.hidden = true;
      }
    }

    col.dataset.selectedType = type;
    updateBadges(col, type, productHasSmartToggle(product));

    chartRoot.querySelectorAll('.cmp-chart__row[data-field-key]').forEach((rowEl) => {
      const key = rowEl.dataset.fieldKey;
      const cell = rowEl.querySelectorAll('.cmp-chart__row-cell')[columnIndex];
      if (!cell) return;
      cell.innerHTML = formatCell(findRow(product, type, key), product.title);
    });
  }

  function buildCompareHTML(products, options) {
    const showTypeSelector = !options || options.showTypeSelector !== false;
    const caption = (options && options.caption) || 'Specification comparison';
    const selectedTypes = {};
    products.forEach((p) => {
      selectedTypes[p.handle] = getInitialType(p);
    });

    let html = `<table class="cmp-chart" style="--cmp-cols: ${products.length}">`;
    html += `<caption class="visually-hidden">${escapeHtml(caption)}</caption>`;
    html += '<colgroup>';
    html += '<col class="cmp-chart__col-label">';
    products.forEach(() => {
      html += '<col class="cmp-chart__col-product">';
    });
    html += '</colgroup>';

    html += '<thead>';
    html += '<tr class="cmp-chart__header">';
    html += '<td class="cmp-chart__label-col"></td>';
    products.forEach((p) => {
      const type = selectedTypes[p.handle];
      const variant = findVariantByType(p.variants || [], type);
      const img = variant.image || p.image;
      const price = variant.price || p.price;
      const comparePrice = variant.compare_at_price || p.compare_at_price;

      html += `<td class="cmp-chart__product-col" data-handle="${escapeAttr(p.handle)}" data-selected-type="${type}">`;
      html += '<div class="cmp-chart__product-inner">';
      html += `<img src="${escapeAttr(img)}" alt="" class="cmp-chart__product-img" width="250" height="250" loading="lazy">`;
      if (showTypeSelector) {
        html += buildBadges(p, type);
      }
      html += `<strong class="cmp-chart__product-name">${p.title}</strong>`;
      html += '<div class="cmp-chart__price-row">';
      html += `<product-price class="cmp-chart__price-wrap" data-product-id="${p.product_id || ''}" data-iom-collections="${p.collections || ''}">`;
      html += `<span class="cmp-chart__price price" data-product-type="price">${price}</span>`;
      html += '</product-price>';
      if (comparePrice) {
        html += `<s class="cmp-chart__price-was">${comparePrice}</s>`;
      }
      html += '</div>';
      html += `<a href="${escapeAttr(p.url)}" class="cmp-chart__view-btn cmp-chart__view-btn--inline">View details</a>`;
      html += '</div>';
      html += '</td>';
    });
    html += '</tr>';
    html += '</thead>';

    html += '<tbody>';
    const rowOrder = buildRowOrder(products);
    rowOrder.forEach((def, idx) => {
      const rowClass = idx % 2 === 0 ? 'cmp-chart__row--even' : 'cmp-chart__row--odd';
      html += `<tr class="cmp-chart__row ${rowClass}" data-field-key="${escapeAttr(def.key)}">`;
      html += `<th class="cmp-chart__row-label" scope="row">${def.label}</th>`;
      products.forEach((p) => {
        const row = findRow(p, selectedTypes[p.handle], def.key);
        html += `<td class="cmp-chart__row-cell">${formatCell(row, p.title)}</td>`;
      });
      html += '</tr>';
    });

    html += '<tr class="cmp-chart__row cmp-chart__row--actions">';
    html += `<td class="cmp-chart__row-label"><span class="visually-hidden">Actions</span></td>`;
    products.forEach((p) => {
      const title = escapeHtml(stripHtml(p.title));
      html += `<td class="cmp-chart__row-cell"><span class="visually-hidden">${title}: </span><a href="${escapeAttr(p.url)}" class="cmp-chart__view-btn">View details</a></td>`;
    });
    html += '</tr>';
    html += '</tbody>';

    html += '</table>';
    return html;
  }

  function bindBadgeInteractions(root, products) {
    const chartRoot = root.querySelector('.cmp-chart');
    if (!chartRoot) return;

    const columns = [...chartRoot.querySelectorAll('.cmp-chart__product-col')];

    columns.forEach((col, columnIndex) => {
      const handle = col.dataset.handle;
      const product = products.find((p) => p.handle === handle);
      if (!product || !productHasSmartToggle(product)) return;

      col.querySelectorAll('.cmp-badge[data-type]').forEach((badge) => {
        badge.addEventListener('click', () => {
          const type = badge.dataset.type;
          if (!type || col.dataset.selectedType === type) return;
          updateColumn(product, col, type, columnIndex, chartRoot);
        });
      });
    });
  }

  window.AirDoctorCompareChart = {
    buildCompareHTML,
    bindBadgeInteractions,
    normalizeSpecs,
    getMergedRows,
  };
})();
